import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getTool, TOOLS } from "@/lib/tools-config";
import { breadcrumbLd, faqLd, SITE_OG_IMAGE, pageTitle, toolPageLd } from "@/lib/seo";
import { FaqBlock } from "@/components/FaqBlock";
import { validateCalcSearch, hasCalcState } from "@/lib/calc-url-state";
import {
  FuelCostCalculator,
  MpgCalculator,
  TripCostCalculator,
  PayloadCalculator,
  TrailerWeightDistributionCalculator,
  EvChargingCalculator,
  GasVsEvCostCalculator,
} from "@/components/tools/Calculators";

export const Route = createFileRoute("/tools/$slug")({
  validateSearch: (search: Record<string, unknown>) => validateCalcSearch(search),
  loader: ({ params }) => {
    const tool = getTool(params.slug);
    if (!tool) throw notFound();
    return { tool };
  },
  head: ({ params, loaderData, match }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Tool unavailable | AutoSpecData" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }
    const { tool } = loaderData;
    const path = `https://autospecdata.com/tools/${params.slug}`;
    return {
      meta: [
        { title: pageTitle(`${tool.h1} — Free & Instant`) },
        { name: "description", content: tool.description },
        { property: "og:title", content: tool.h1 },
        { property: "og:description", content: tool.description },
        { property: "og:url", content: path },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: SITE_OG_IMAGE },
        { name: "twitter:image", content: SITE_OG_IMAGE },
        ...(hasCalcState(match.search as Record<string, unknown>)
          ? [{ name: "robots", content: "noindex, follow" }]
          : []),
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            toolPageLd({
              name: tool.h1,
              path,
              description: tool.description,
            }),
          ),
        },

        {
          type: "application/ld+json",
          children: JSON.stringify(
            faqLd([
              { q: `How does the ${tool.name.toLowerCase()} work?`, a: tool.answer },
              ...(tool.faqs ?? []),
              {
                q: "Is it free?",
                a: "Yes. The calculator runs entirely in your browser, needs no sign-up, and nothing you enter is stored or sent anywhere.",
              },
              {
                q: "Where can I find my vehicle's specs?",
                a: "Look it up on its AutoSpecData model page — every figure comes from official manufacturer and EPA data, broken out by year, engine and drivetrain.",
              },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: "Tools", path: "/tools" },
              { name: tool.name, path },
            ]),
          ),
        },
      ],
    };
  },
  component: ToolPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-2xl font-bold text-foreground">Tool not found</h1>
      <Link to="/tools" className="mt-6 inline-block text-primary underline">
        See all tools
      </Link>
    </div>
  ),
});

function ToolPage() {
  const { tool } = Route.useLoaderData();
  const faqs = [
    { q: `How does the ${tool.name.toLowerCase()} work?`, a: tool.answer },
    ...(tool.faqs ?? []),
    {
      q: "Is it free?",
      a: "Yes. The calculator runs entirely in your browser, needs no sign-up, and nothing you enter is stored or sent anywhere.",
    },
    {
      q: "Where can I find my vehicle's specs?",
      a: "Look it up on its AutoSpecData model page — every figure comes from official manufacturer and EPA data, broken out by year, engine and drivetrain.",
    },
  ];
  return (
    <div>
      <nav className="mx-auto max-w-4xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <Link to="/tools" className="hover:text-foreground">
          Tools
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{tool.name}</span>
      </nav>

      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">{tool.h1}</h1>
          <p
            data-quick-answer
            className="mt-4 max-w-3xl text-base sm:text-lg leading-relaxed text-foreground"
          >
            {tool.answer}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
        {tool.slug === "fuel-cost-calculator" ? <FuelCostCalculator /> : null}
        {tool.slug === "mpg-calculator" ? <MpgCalculator /> : null}
        {tool.slug === "trip-cost-calculator" ? <TripCostCalculator /> : null}
        {tool.slug === "payload-calculator" ? <PayloadCalculator /> : null}
        {tool.slug === "trailer-weight-distribution-calculator" ? (
          <TrailerWeightDistributionCalculator />
        ) : null}
        {tool.slug === "ev-charging-calculator" ? <EvChargingCalculator /> : null}
        {tool.slug === "gas-vs-ev-cost-calculator" ? <GasVsEvCostCalculator /> : null}

        <h2 className="mt-10 text-xl font-bold text-foreground">
          How to use the {tool.name.toLowerCase()}
        </h2>
        <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
          <p>
            Enter your own numbers in the fields above and the result updates instantly — there is
            no submit step, no account, and no data leaves your browser. Everything is computed
            client-side from the values you type, so you can try several scenarios back to back
            and compare the outcomes without reloading the page.
          </p>
          <p>
            For the most realistic result, use figures that reflect how you actually drive rather
            than best-case numbers. Fuel prices vary by state and by season, annual mileage is
            usually higher than people estimate, and EPA combined MPG is a laboratory rating that
            most drivers fall roughly 10-15% short of in mixed real-world use — more in cold
            weather, heavy traffic, or at sustained highway speeds. Running the calculation twice,
            once with optimistic inputs and once with conservative ones, gives you a realistic
            range instead of a single misleading number.
          </p>
          <p>
            Treat the output as a planning estimate, not a quote. It does not include insurance,
            registration, financing interest, depreciation, or unscheduled repairs, all of which
            can move total ownership cost more than fuel does over a five-year window.
          </p>
        </div>

        <h2 className="mt-10 text-xl font-bold text-foreground">Frequently asked questions</h2>
        <div className="mt-4">
          <FaqBlock items={faqs} />
        </div>

        <h2 className="mt-10 text-xl font-bold text-foreground">Don't know your MPG?</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Every vehicle on AutoSpecData lists its official EPA city, highway and combined ratings
          year by year.{" "}
          <Link
            to="/search"
            search={{ q: "", make: "", model: "", year: undefined, type: "", page: 1 }}
            className="cta-gold"
          >
            Search your vehicle
          </Link>{" "}
          or start from the{" "}
          <Link to="/best" className="text-primary underline">
            fuel economy rankings
          </Link>
          .
        </p>

        <h2 className="mt-10 text-xl font-bold text-foreground">Other calculators</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {TOOLS.filter((t) => t.slug !== tool.slug).map((t) => (
            <Link
              key={t.slug}
              to="/tools/$slug"
              params={{ slug: t.slug }}
              className="rounded-full border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              {t.name}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
